/**
 * IdeasList Component
 * 
 * Displays a filterable list of ideas for an opportunity.
 * Includes header with add button and status filters.
 */

import React, { useMemo } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { IdeaWithDetails, IdeaStatus } from '../../../shared/types/idea';
import { IdeaCard } from './IdeaCard';
import { styles } from './styles/ideasListStyles';

interface IdeasListProps {
  ideas: IdeaWithDetails[];
  loading: boolean;
  error: string | null;
  filter: IdeaStatus | 'all';
  onFilterChange: (filter: IdeaStatus | 'all') => void;
  onIdeaPress: (idea: IdeaWithDetails) => void;
  onAddPress: () => void;
  onRefresh: () => void;
}

const FILTER_OPTIONS: { key: IdeaStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'under_review', label: 'Reviewing' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'implemented', label: 'Done' },
];

export function IdeasList({
  ideas, loading, error, filter, onFilterChange, onIdeaPress, onAddPress, onRefresh,
}: IdeasListProps) {
  // Filter ideas
  const filteredIdeas = useMemo(() => {
    if (filter === 'all') return ideas;
    return ideas.filter(idea => idea.status === filter);
  }, [ideas, filter]);

  // Group by status for counts
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: ideas.length,
      open: 0, under_review: 0, approved: 0, rejected: 0, implemented: 0, deferred: 0,
    };
    ideas.forEach(idea => { 
      if (counts[idea.status] !== undefined) counts[idea.status]++; 
    });
    return counts;
  }, [ideas]);

  // Error state
  if (error && ideas.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={onRefresh} style={styles.retryButton}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={color.primary} />}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Ideas</Text>
          <Pressable onPress={onAddPress} style={styles.addButton}>
            <Ionicons name="add" size={icon.md} color={color.surface} />
            <Text style={styles.addButtonText}>New Idea</Text>
          </Pressable>
        </View>

        {/* Filters */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll} contentContainerStyle={styles.filtersContainer}>
          {FILTER_OPTIONS.map(opt => (
            <Pressable
              key={opt.key}
              onPress={() => onFilterChange(opt.key)}
              style={[styles.filterChip, filter === opt.key && styles.filterChipActive]}
            >
              <Text style={[styles.filterChipText, filter === opt.key && styles.filterChipTextActive]}>
                {opt.label} ({statusCounts[opt.key]})
              </Text>
            </Pressable>
          ))}
        </ScrollView>

        {/* Loading state */}
        {loading && ideas.length === 0 && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={color.primary} />
            <Text style={styles.loadingText}>Loading ideas...</Text>
          </View>
        )}

        {/* Empty state */}
        {!loading && filteredIdeas.length === 0 && (
          <View style={styles.emptyContainer}>
            <View style={styles.emptyIcon}>
              <Ionicons name="bulb-outline" size={icon.xxl} color={color.primary} />
            </View>
            <Text style={styles.emptyTitle}>
              {filter === 'all' ? 'No ideas yet' : `No ${FILTER_OPTIONS.find(f => f.key === filter)?.label.toLowerCase()} ideas`}
            </Text>
            <Text style={styles.emptyText}>
              {filter === 'all' 
                ? 'Start collaborating by sharing your thoughts and suggestions for this opportunity.'
                : 'Try changing the filter to see other ideas.'
              }
            </Text>
            {filter === 'all' && (
              <Pressable onPress={onAddPress} style={styles.emptyButton}>
                <Text style={styles.emptyButtonText}>Share Your First Idea</Text>
              </Pressable>
            )}
          </View>
        )}

        {/* Ideas List */}
        {filteredIdeas.length > 0 && (
          <View style={styles.listContainer}>
            {filteredIdeas.map(idea => (
              <IdeaCard key={idea.id} idea={idea} onPress={() => onIdeaPress(idea)} />
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
}
